import {AxiosInstance, AxiosResponse} from 'axios';
import {jwtDecode, JwtPayload} from "jwt-decode";
import {StorageService} from "@/core/services/StorageService";
import {useSystemStore} from "@/stores/system.store";

interface User {
    currentAccess: {
        organization: {
            morphable_type: string;
            rankId: number;
        };
    };
    token: string;
    email: string;
    first_name?: string;
    last_name?: string;
}

interface Response {
    data: {
        user: User;
        token: string;
        device_token?: string;
    };
}

interface DeviceIdentity {
    host_name?: string;
    device_token?: string;
}

export default function (client: AxiosInstance, storageService: StorageService) {
    /**
     * Describe this workstation to the API so it can tell a known counter PC
     * from an unknown machine. `device_token` is a secret the API issued on a
     * previous trusted login; presenting it is what keeps this device trusted.
     *
     * Never sends `supports_two_factor`: that flag tells the API this client
     * can answer a step-up challenge, which it cannot do yet.
     */
    const deviceIdentity = async (): Promise<DeviceIdentity> => {
        const identity: DeviceIdentity = {};

        if (window.ipcRenderer) {
            try {
                identity.host_name = await useSystemStore().getHostname();
            } catch {
                // A login must never fail because the host name is unreadable.
            }
        }

        const deviceToken = await storageService.read('deviceToken') as string | null;

        if (deviceToken) {
            identity.device_token = deviceToken;
        }

        return identity;
    };

    return {
        /**
         * Check if user is authenticated by checking if token is valid in local storage.
         * check if a legacy token is present in localStorage for backward compatibility
         */
        async hasToken(): Promise<boolean> {
            let token = await storageService.read('token') as string | null;

            if (!token) {
                const legacyToken = localStorage.getItem('token');

                if (legacyToken) {
                    token = legacyToken;
                    await storageService.store('token', legacyToken);
                } else {
                    return false;
                }
            }

            const decoded = jwtDecode<JwtPayload>(token);

            return (decoded?.exp || 0) * 1000 > Date.now();
        },

        /**
         * Read user from jwt.
         */
        async user(): Promise<User | undefined> {
            const token = await storageService.read('token') as string | null;

            if (token) {
                const decoded = jwtDecode<JwtPayload & { user: User }>(token);

                return decoded?.user;
            }
        },

        /**
         * Read access from jwt.
         */
        async access(): Promise<object | undefined> {
            const token = await storageService.read('token') as string | null;

            if (token) {
                const decoded = jwtDecode<JwtPayload & { access: object }>(token);

                return decoded?.access;
            }
        },

        /**
         * Login as a given user, using an authentication key.
         * @param key  The authentication key.
         * @param access_id  The access id.
         */
        async loginAs(key: string, access_id: number): Promise<User | null> {
            const response = await client.post('/authenticate', {key, access_id}, {
                params: {
                    include: 'currentAccess.organization'
                }
            });

            await storageService.store('token', response.data.token);

            return response.data.user;
        },

        /**
         * Login with email and password.
         * @param email
         * @param password
         */
        async login(email: string, password: string): Promise<User | null> {
            return await client
                .post<Response>('/authenticate', await deviceIdentity(), {
                    params: {
                        include: 'currentAccess.organization'
                    },

                    auth: {
                        username: email,
                        password: password
                    }
                })
                .then(async (response: AxiosResponse) => {
                    const organization = response.data
                        .user
                        .currentAccess
                        .organization;

                    if (organization.morphable_type !== 'healthcenter') {
                        throw new Error('Sélectionnez un environnement « pharmacie » sur Apodis');
                    }

                    if (response.data.user.currentAccess.rankId !== 1 && response.data.user.currentAccess.rankId !== 2) {
                        return null;
                    } else {
                        await storageService.store('token', response.data.token);

                        if (response.data.device_token) {
                            await storageService.store('deviceToken', response.data.device_token);
                        }
                    }

                    return organization;
                });
        },

        /**
         * Logout the user.
         *
         * Keeps `deviceToken` on purpose: it is the workstation's trust, not
         * the user's session. Clearing it here would make the API treat this
         * counter PC as a brand new machine at the next login.
         */
        async logout(): Promise<void> {
            const {unsubscribeEvent} = useSystemStore();
            if (unsubscribeEvent) {
                unsubscribeEvent();
            }

            await storageService.destroy('token');
        },

        /**
         * Start the password recovery process.
         * @param {string} email - The email address of the user.
         */
        async forgotPassword(email: string): Promise<object> {
            return await client
                .post('/users/forgotPassword', {email})
                .then(response => {
                    return response.data;
                });
        }
    };
}
